import { Fragment, useEffect, useState } from 'react'
import api from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog } from '@/components/ui/dialog'
import { Steps } from '@/components/ui/steps'
import { useToast } from '@/components/ui/toast'
import { formatBRL, formatDate } from '@/lib/utils'

const STEPS = [
  { key: 'periodo', label: 'Período' },
  { key: 'confirmar', label: 'Confirmar' },
]

/** Datas do mês anterior (primeiro e último dia). */
function mesAnterior() {
  const hoje = new Date()
  const ini = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1)
  const fim = new Date(hoje.getFullYear(), hoje.getMonth(), 0)
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { inicio: iso(ini), fim: iso(fim) }
}

export default function GerarRascunhoModal({ open, onClose, onDone }) {
  const toast = useToast()
  const [periodo, setPeriodo] = useState(mesAnterior())
  const [step, setStep] = useState('periodo') // periodo | confirmar
  const [preview, setPreview] = useState([])
  const [marcados, setMarcados] = useState({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open) {
      setPeriodo(mesAnterior())
      setStep('periodo')
      setPreview([])
      setMarcados({})
    }
  }, [open])

  async function carregarPreview() {
    setLoading(true)
    try {
      const { data } = await api.get('/nfs/rascunhos/preview', { params: periodo })
      setPreview(data.data)
      setMarcados(Object.fromEntries(data.data.map((n) => [n.nfID, true])))
      setStep('confirmar')
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Não foi possível carregar as NFs do período.')
    } finally {
      setLoading(false)
    }
  }

  async function gerar() {
    const nfIDs = preview.filter((n) => marcados[n.nfID]).map((n) => n.nfID)
    if (nfIDs.length === 0) {
      toast.error('Selecione ao menos uma NF.')
      return
    }
    setLoading(true)
    try {
      const { data } = await api.post('/nfs/rascunhos', { nfIDs })
      toast.success(`${data.criados} rascunho(s) gerado(s).`)
      onDone()
    } catch {
      toast.error('Erro ao gerar os rascunhos.')
    } finally {
      setLoading(false)
    }
  }

  const totalMarcados = preview.filter((n) => marcados[n.nfID]).length
  const todosMarcados = preview.length > 0 && totalMarcados === preview.length

  function alternarTodos() {
    const novo = !todosMarcados
    setMarcados(Object.fromEntries(preview.map((n) => [n.nfID, novo])))
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Gerar rascunhos em lote"
      description="Duplica as NFs emitidas no período selecionado como novos rascunhos."
      size={step === 'periodo' ? 'md' : 'lg'}
      footer={step === 'periodo' ? (
        <>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
          <Button onClick={carregarPreview} disabled={loading}>{loading ? 'Carregando...' : 'Gerar rascunhos'}</Button>
        </>
      ) : (
        <>
          <Button variant="outline" onClick={() => setStep('periodo')}>Voltar</Button>
          <Button onClick={gerar} disabled={loading || totalMarcados === 0}>
            {loading ? 'Gerando...' : `Gerar ${totalMarcados} rascunho(s)`}
          </Button>
        </>
      )}
    >
      <Steps steps={STEPS} current={step} />

      {step === 'periodo' ? (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Data inicial</Label>
            <Input type="date" value={periodo.inicio} onChange={(e) => setPeriodo((p) => ({ ...p, inicio: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Data final</Label>
            <Input type="date" value={periodo.fim} onChange={(e) => setPeriodo((p) => ({ ...p, fim: e.target.value }))} />
          </div>
        </div>
      ) : (
        <div>
          {preview.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Nenhuma NF emitida encontrada no período.</p>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="max-h-80 overflow-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground bg-muted/50 sticky top-0">
                    <tr>
                      <th className="w-10 px-3 py-2">
                        <input
                          type="checkbox"
                          checked={todosMarcados}
                          onChange={alternarTodos}
                          title={todosMarcados ? 'Desmarcar todas' : 'Marcar todas'}
                          className="h-4 w-4 align-middle accent-[var(--primary)]"
                        />
                      </th>
                      <th className="px-3 py-2 font-medium">Cliente</th>
                      <th className="px-3 py-2 font-medium">Emissão</th>
                      <th className="px-3 py-2 font-medium">Contrato</th>
                      <th className="px-3 py-2 font-medium text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((n) => (
                      <Fragment key={n.nfID}>
                        <tr className="border-t border-border/60">
                          <td className="px-3 pt-2 align-top">
                            <input
                              type="checkbox"
                              checked={!!marcados[n.nfID]}
                              onChange={(e) => setMarcados((m) => ({ ...m, [n.nfID]: e.target.checked }))}
                              className="h-4 w-4 align-middle accent-[var(--primary)]"
                            />
                          </td>
                          <td className="px-3 pt-2 align-top font-medium">{n.clienteNome}</td>
                          <td className="px-3 pt-2 align-top whitespace-nowrap text-muted-foreground">{n.dataEmissao ? formatDate(n.dataEmissao) : '—'}</td>
                          <td className="px-3 pt-2 align-top whitespace-nowrap text-muted-foreground">{n.numeroContrato}</td>
                          <td className="px-3 pt-2 align-top text-right whitespace-nowrap tabular-nums">{formatBRL(n.valor)}</td>
                        </tr>
                        <tr>
                          <td></td>
                          <td colSpan={4} className="px-3 pb-2 pt-0.5 text-xs text-muted-foreground/80 whitespace-pre-line break-words">
                            {n.descricaoServico}
                          </td>
                        </tr>
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-2 border-t border-border bg-muted/30 text-xs text-muted-foreground">
                {totalMarcados} de {preview.length} selecionada(s)
              </div>
            </div>
          )}
        </div>
      )}
    </Dialog>
  )
}
